import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Plus, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import InstagramPanel from '../components/admin/InstagramPanel';
import PostEditor from '../components/admin/PostEditor';
import PostList from '../components/admin/PostList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  title: '',
  title_fr: '',
  excerpt: '',
  excerpt_fr: '',
  content: '',
  content_fr: '',
  hero_image: '',
  category: 'Wellness',
  slug: '',
  meta_description: '',
  meta_description_fr: ''
};

function Admin() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [posts, setPosts] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [igStatus, setIgStatus] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts`);
      setPosts(response.data);
    } catch {
      // silently fail
    }
  }, []);

  const fetchIgStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/instagram/status`);
      setIgStatus(resp.data);
    } catch {
      setIgStatus({ connected: false });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
      fetchIgStatus();
    }
  }, [isAuthenticated, fetchPosts, fetchIgStatus]);

  useEffect(() => {
    if (searchParams.get('ig_connected') === 'true') {
      toast.success('Instagram connected successfully!');
      if (isAuthenticated) fetchIgStatus();
    }
    if (searchParams.get('ig_error')) {
      toast.error(`Instagram connection failed: ${searchParams.get('ig_error')}`);
    }
  }, [searchParams, isAuthenticated, fetchIgStatus]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/login`, credentials);
      setIsAuthenticated(true);
      toast.success('Logged in successfully');
    } catch {
      toast.error('Invalid credentials');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API_URL}/api/admin/blog/posts/${editingPost.id}`, formData);
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/blog/posts`, formData);
        toast.success('Post created successfully');
      }
      fetchPosts();
      resetForm();
    } catch {
      toast.error('Failed to save post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      title_fr: post.title_fr,
      excerpt: post.excerpt,
      excerpt_fr: post.excerpt_fr,
      content: post.content,
      content_fr: post.content_fr,
      hero_image: post.hero_image,
      category: post.category,
      slug: post.slug || '',
      meta_description: post.meta_description || '',
      meta_description_fr: post.meta_description_fr || ''
    });
    setShowEditor(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await axios.delete(`${API_URL}/api/admin/blog/posts/${id}`);
        toast.success('Post deleted successfully');
        fetchPosts();
      } catch {
        toast.error('Failed to delete post');
      }
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingPost(null);
    setShowEditor(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center py-24" data-testid="admin-login">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-[0_8px_32px_rgba(59,48,45,0.04)] border border-accent/30">
            <h1 className="font-playfair text-4xl font-medium text-charcoal mb-8 text-center">
              {t('admin.title')}
            </h1>
            <form onSubmit={handleLogin} data-testid="login-form">
              <div className="mb-6">
                <label htmlFor="username" className="block text-charcoal font-medium mb-2">
                  {t('admin.username')}
                </label>
                <input
                  type="text"
                  id="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                  data-testid="username-input"
                />
              </div>
              <div className="mb-6">
                <label htmlFor="password" className="block text-charcoal font-medium mb-2">
                  {t('admin.password')}
                </label>
                <input
                  type="password"
                  id="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                  data-testid="password-input"
                />
              </div>
              <button
                type="submit"
                className="w-full px-8 py-4 rounded-full bg-primary hover:bg-charcoal text-white transition-colors duration-300 font-medium"
                data-testid="login-btn"
              >
                {t('admin.login')}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page min-h-screen bg-warm-white py-24" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-medium text-charcoal">
            {t('admin.title')}
          </h1>
          <button
            onClick={() => setIsAuthenticated(false)}
            className="flex items-center space-x-2 px-6 py-3 rounded-full border border-primary text-primary hover:bg-accent/20 transition-colors duration-300"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('admin.logout')}</span>
          </button>
        </div>

        <InstagramPanel igStatus={igStatus} onStatusChange={setIgStatus} />

        {showEditor ? (
          <PostEditor
            formData={formData}
            setFormData={setFormData}
            editingPost={editingPost}
            onSubmit={handleSubmit}
            onCancel={resetForm}
          />
        ) : (
          <button
            onClick={() => setShowEditor(true)}
            className="flex items-center space-x-2 px-8 py-4 rounded-full bg-primary hover:bg-charcoal text-white transition-colors duration-300 font-medium mb-8"
            data-testid="create-post-btn"
          >
            <Plus className="w-5 h-5" />
            <span>{t('admin.createPost')}</span>
          </button>
        )}

        <PostList posts={posts} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </div>
  );
}

export default Admin;
